
import { Component, Vue } from "vue-property-decorator";

import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import vModal from "@/components/vModal.vue";
import { UserTypes } from "@/models/User/UserTypes";
import vTable from "@/components/vTable.vue";
import LocalizationRepository from "../../../utils/API/LocalizationRepository";
import Translations from "../../../models/Localization/Translations";
import { RestaurantEmployee } from "../../../models/Customer/RestaurantEmployee";
import { UpdateRestaurantEmployee } from "../../../models/Customer/UpdateRestaurantEmployee";
import { CreateRestaurantEmployee } from "../../../models/Customer/CreateRestaurantEmployee";
import CustomersRepository from "../../../utils/API/CustomersRepository";
import vToggleSwitch from "@/components/vToggleSwitch.vue";

interface RestaurantUsersState {
  /**
   * Number of packages displayed on the grid
   */
  count: number;
  /**
   * Total entries in the grid
   */
  totalCount: number;
  /**
   * List of restaurant users in the system determined by page and page size
   */
  restaurantUsers: RestaurantEmployee[];
  /**
   * Current page on the grid
   */
  page: number;
  /**
   * Maximum visible rows in the grid
   */
  pageSize: number;
  /**
   * List of filters
   */
  filters: Filter[];
  /**
   * Ordering
   */
  orders: Ordering;

  /**
   * Model for restaurant user creation
   */
  createRestaurantUser: CreateRestaurantEmployee;
  /**
   * Model for restaurant user edit
   */
  updateRestaurantUser: UpdateRestaurantEmployee;
  /**
   * List of restaurant users to be removed
   */
  removeList: number[];

  /**
   * Is the grid loading?
   */
  tableLoading: boolean;
}

@Component({
  components: {
    vModal,
    vTable,
    vToggleSwitch
  }
})
class RestaurantUsersManagement extends Vue {
  // NOTE: It's local, not from global state
  localizationState: any = {
    totalCount: 0,
    count: 0,
    translations: new Translations()
  };

  restaurantUsersState: RestaurantUsersState = {
    count: 0,
    totalCount: 0,
    restaurantUsers: [],
    page: 1,
    pageSize: 25,
    filters: [],
    orders: { field: "", orderType: OrderType.Asc },
    createRestaurantUser: {
      restaurantId: 0,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      isEnabled: true,
      userType: UserTypes.Chef
    },
    updateRestaurantUser: {
      userId: 0,
      firstName: "",
      lastName: "",
      email: "",
      isEnabled: true,
      userType: UserTypes.Chef,
      password: "",
      confirmPassword: ""
    },
    removeList: [],

    tableLoading: false
  };

  // Properties for vTable grid
  selectErase = false;
  searchable = true;
  editableTable = true;

  // Modal properties
  showModal = false;
  modalTitle = "";
  showRemovePrompt = false;
  removePromptTitle = "";

  editMode = false;

  /**
   * Method used by the grid after catching the 'changePage'
   * event from vTable
   *
   * @param {any} args
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });

    this.requestRestaurantUsers(
      new ListRequest({
        page: args[0],
        pageSize: this.restaurantUsersState.pageSize,
        filters: args[2],
        orders: orderings
      })
    );
    this.restaurantUsersState.page = args[0];
    this.restaurantUsersState.orders = { ...args[3] };
    this.restaurantUsersState.filters = [...args[2]];
  }

  /**
   * Method used by the grid after catching the 'search'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  search(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestRestaurantUsers(
      new ListRequest({
        page: 1,
        pageSize: this.restaurantUsersState.pageSize,
        filters: filters,
        orders: orderings
      })
    );

    this.restaurantUsersState.page = 1;
    this.restaurantUsersState.orders = { ...ordering };
    this.restaurantUsersState.filters = [...filters];
  }

  /**
   * Method used by the grid after catching the 'order'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestRestaurantUsers(
      new ListRequest({
        page: this.restaurantUsersState.page,
        pageSize: this.restaurantUsersState.pageSize,
        filters: filters,
        orders: orderings
      })
    );

    this.restaurantUsersState.orders = { ...ordering };
    this.restaurantUsersState.filters = [...filters];
  }

  /**
   * Method that resets most variables and closes all modals.
   */
  resetState() {
    this.showModal = false;
    this.editMode = false;
    this.modalTitle = this.translations.get("resUsrMgm_modal_title_add");

    this.showRemovePrompt = false;
    this.removePromptTitle = "";

    this.restaurantUsersState.updateRestaurantUser = {
      userId: 0,
      firstName: "",
      lastName: "",
      email: "",
      isEnabled: true,
      userType: UserTypes.Chef,
      password: "",
      confirmPassword: ""
    };

    this.restaurantUsersState.createRestaurantUser = {
      restaurantId: 0,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      isEnabled: true,
      userType: UserTypes.Chef
    };
  }

  /**
   * Method for edit / add restaurant users modal toggling
   */
  toggleModal() {
    this.showModal = !this.showModal;
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("email"),
        key: "Email",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("first_name"),
        key: "FirstName",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("last_name"),
        key: "LastName",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("user_type"),
        key: "UserType",
        searchable: false,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("enabled"),
        key: "IsEnabled",
        searchable: false,
        searchString: "",
        sortable: true
      }
    ];
  }

  /**
   * Setting modal title for Adding new restaurant user and toggling the modal
   */
  prepareAddMode() {
    this.modalTitle = this.translations.get("resUsrMgm_modal_title_add");
    this.toggleModal();
  }

  /**
   * Populating form and toggling the modal for editing restaurant user.
   * Sets the edit mode to true and toggling the modal
   *
   * @param {number} id Id of the package
   */
  prepareEditMode(id: number) {
    this.editMode = true;
    this.modalTitle = this.translations.get("resUsrMgm_modal_title_edit");

    let found = this.restaurantUsersState.restaurantUsers.find(
      (au) => au.id === id
    );

    if (typeof found !== "undefined") {
      let temp: UpdateRestaurantEmployee = {
        isEnabled: found.isEnabled ? found.isEnabled : false,
        userId: found.id ? found.id : 0,
        firstName: found.firstName ? found.firstName : "",
        lastName: found.lastName ? found.lastName : "",
        email: found.email ? found.email : "",
        userType: found.userType !== null ? found.userType : UserTypes.Chef,
        password: "",
        confirmPassword: ""
      };

      this.restaurantUsersState.updateRestaurantUser = { ...temp };

      this.toggleModal();
    }
  }

  /**
   * Method for displaying prompt message for selected restaurant users to be deleted
   *
   * @param {number[]} ids
   */
  prepareDeletePrompt() {
    this.removePromptTitle = this.translations.get(
      "resUsrMgm_delete_prompt_message", this.restaurantUsersState.updateRestaurantUser.firstName
    );
    this.showRemovePrompt = true;
  }

  /**
   * Method for deleting selected restaurant users and refreshing the grid.
   */
  confirmDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("resUsrMgm_toastr_deleting"),
      () =>
        new Promise((resolve, reject) => {
          CustomersRepository.deleteRestaurantEmployee(
            this.restaurantUsersState.updateRestaurantUser.userId
          )
            .then(() => {
              resolve({
                body: this.translations.get("resUsrMgm_toastr_deleted", this.restaurantUsersState.updateRestaurantUser.firstName)
              });

              this.restaurantUsersState.page = 1;

              this.requestRestaurantUsers(
                new ListRequest({
                  page: this.restaurantUsersState.page,
                  pageSize: this.restaurantUsersState.pageSize,
                  filters: this.restaurantUsersState.filters,
                  orders: [this.restaurantUsersState.orders]
                })
              );

              this.resetState();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
  }

  /**
   * Router method for submitting to API
   */
  submit() {
    this.$validator.validateAll('resUsrMgm').then((result) => {

      if (result) {
        if (this.editMode) {
          this.submitUpdate();
        }
        else {
          this.submitNew();
        }
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  /**
   * Method for submitting edit to api and refreshing the grid
   */
  submitUpdate() {
    let adminUser = this.restaurantUsersState.restaurantUsers.find(
      (adminUser) =>
        adminUser.id === this.restaurantUsersState.updateRestaurantUser.userId
    );

    if (
      typeof adminUser !== "undefined" &&
      (adminUser.firstName !==
        this.restaurantUsersState.updateRestaurantUser.firstName ||
        adminUser.lastName !==
        this.restaurantUsersState.updateRestaurantUser.lastName ||
        adminUser.isEnabled !==
        this.restaurantUsersState.updateRestaurantUser.isEnabled ||
        adminUser.email !==
        this.restaurantUsersState.updateRestaurantUser.email ||
        adminUser.userType !==
        this.restaurantUsersState.updateRestaurantUser.userType)
    ) {
      // @ts-ignore
      this.$snotify.async(
        this.translations.get("resUsrMgm_toastr_updating"),
        () =>
          new Promise((resolve, reject) => {
            CustomersRepository.updateRestaurantEmployee(
              this.restaurantUsersState.updateRestaurantUser
            )
              .then(() => {
                resolve({
                  body: this.translations.get(
                    "resUsrMgm_toastr_updated",
                    this.restaurantUsersState.updateRestaurantUser.firstName
                  )
                });

                this.requestRestaurantUsers(
                  new ListRequest({
                    page: this.restaurantUsersState.page,
                    pageSize: this.restaurantUsersState.pageSize,
                    filters: this.restaurantUsersState.filters,
                    orders: [this.restaurantUsersState.orders]
                  })
                );

                this.resetState();
              })
              .catch((error) => {
                let message = "";
                if (error.response) {
                  message =
                    error.response.data.errorMessages[0].userVisibleMessage;
                }
                else {
                  message = this.translations.get("network_error");
                }
                reject({ body: message });
              });
          }),
        {
          timeout: 3000,
          closeOnClick: true
        }
      );
    }
    else {
      this.$snotify.simple(this.translations.get("toastr_nochanges"));
      this.resetState();
    }
  }

  /**
   * Method for submitting new coupon to api and refreshing the grid
   */
  submitNew() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("resUsrMgm_toastr_adding"),
      () =>
        new Promise((resolve, reject) => {

          // set restaurant id manually to user's restaurant
          this.restaurantUsersState.createRestaurantUser.restaurantId = this.$store.getters.getUserDetails.restaurantId;

          CustomersRepository.createRestaurantEmployee(
            this.restaurantUsersState.createRestaurantUser
          )
            .then(() => {
              resolve({
                body: this.translations.get(
                  "resUsrMgm_toastr_added",
                  this.restaurantUsersState.createRestaurantUser.firstName
                )
              });

              this.requestRestaurantUsers(
                new ListRequest({
                  page: this.restaurantUsersState.page,
                  pageSize: this.restaurantUsersState.pageSize,
                  filters: this.restaurantUsersState.filters,
                  orders: [this.restaurantUsersState.orders]
                })
              );

              this.resetState();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
  }

  /**
   * Getter method to transform the list of admin users to grid data (vTable)
   */
  get transformRestaurantUsers() {
    return this.restaurantUsersState.restaurantUsers.map(
      (element: RestaurantEmployee) => {
        return {
          e1: element.firstName ? element.firstName : "",
          e2: element.lastName ? element.lastName : "",
          e3: this.translations.get(`user_type_${element.userType}`),
          e4: element.isEnabled
            ? this.translations.get(`enabled`)
            : this.translations.get(`disabled`),
          meta: {
            id: element.id,
            link: element.email
          }
        };
      }
    );
  }

  /**
   * Method for fetching admin users based on list request
   *
   * @param {ListRequest} listRequest
   */
  requestRestaurantUsers(listRequest: ListRequest) {
    this.restaurantUsersState.tableLoading = true;
    CustomersRepository.listRestaurantEmployees(
      listRequest,
      this.$store.getters.getUserDetails.restaurantId
    )
      .then((response) => {
        this.restaurantUsersState.count = response.data.count;
        this.restaurantUsersState.totalCount = response.data.totalCount;
        this.restaurantUsersState.restaurantUsers = response.data.result;

        this.restaurantUsersState.tableLoading = false;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
        this.restaurantUsersState.tableLoading = false;
      });
  }

  mounted() {
    this.requestRestaurantUsers(
      new ListRequest({
        page: this.restaurantUsersState.page,
        pageSize: this.restaurantUsersState.pageSize,
        filters: this.restaurantUsersState.filters,
        orders: [this.restaurantUsersState.orders]
      })
    );
  }

  created() {
    // get translations locally

    let translationsRequest = {
      category: "pages",
      collections: ["resUsrMgm", "common"]
    };
    LocalizationRepository.getTranslations(translationsRequest)
      .then((response) => {
        let translationList = response.data.result;
        let translations = new Translations(translationList);

        this.localizationState = {
          totalCount: response.data.totalCount,
          count: response.data.count,
          translations
        };
      })
      .catch(() => {});
  }

  get translations() {
    return this.localizationState.translations;
  }
}
export default RestaurantUsersManagement;
